import {
  getAllProducts,
  getBestProducts,
  getDealProducts,
  getNewProducts,
  getProductById,
  getRecommendProducts,
  removeLiveProduct,
  setLiveProducts,
  MALL_DEFAULT_SORT,
  sortProducts,
  upsertLiveProduct,
  type MallCategory,
  type MallProduct,
  type MallSortKey,
} from '@/constants/mallProducts'
import { fetchLiveProduct, fetchLiveProducts, toMallProduct } from '@/api/mallProduct'
import { resetSearchIndex } from '@/services/searchIndex'

/**
 * 쇼핑몰 상품 API 레이어.
 * 판매업체가 등록·승인된 실제 상품만 노출한다.
 */
let livePromise: Promise<void> | null = null
let loadedAt = 0

/**
 * 목록을 다시 받아 오는 간격.
 * 관리자가 승인하거나 판매업체가 검수를 다시 요청하면 몰 노출이 바뀐다.
 * 화면을 새로 고치지 않아도 곧 반영되도록 짧게 잡는다.
 */
const LIVE_TTL_MS = 30_000

/**
 * 실제 등록 상품 목록을 받아 둔다.
 * 백엔드 오류 시 빈 목록으로 두고, 화면은 그대로 연다.
 */
export async function ensureLiveProducts(refresh = false): Promise<void> {
  const stale = Date.now() - loadedAt > LIVE_TTL_MS

  if (refresh || (stale && !livePromise)) {
    livePromise = null
  }

  if (!livePromise) {
    livePromise = fetchLiveProducts()
      .then((products) => {
        setLiveProducts(products.map(toMallProduct))
        loadedAt = Date.now()
        /* 통합검색 색인에도 새 상품이 들어가야 한다 */
        resetSearchIndex()
      })
      .catch(() => undefined)
      .finally(() => {
        livePromise = null
      })
  }

  await livePromise
}

export interface MallCatalogQuery {
  category?: '전체' | MallCategory
  keyword?: string
  sort?: MallSortKey
  inStockOnly?: boolean
}

export async function fetchMallCatalog(query: MallCatalogQuery = {}): Promise<MallProduct[]> {
  await ensureLiveProducts()

  const category = query.category ?? '전체'
  const keyword = (query.keyword ?? '').trim()
  const sort = query.sort ?? MALL_DEFAULT_SORT
  const inStockOnly = Boolean(query.inStockOnly)

  const filtered = getAllProducts().filter((product) => {
    const matchCategory = category === '전체' || product.category === category
    const matchKeyword =
      !keyword ||
      product.name.includes(keyword) ||
      product.summary.includes(keyword) ||
      product.tags.some((tag) => tag.includes(keyword))
    const matchStock = !inStockOnly || !product.soldOut
    return matchCategory && matchKeyword && matchStock
  })

  return sortProducts(filtered, sort)
}

export async function fetchMallProduct(id: string): Promise<MallProduct | null> {
  await ensureLiveProducts()

  try {
    const detail = await fetchLiveProduct(id)

    /* 검수 재요청 · 숨김 · 반려로 내려간 상품은 캐시에서도 빼고 없는 것으로 다룬다. */
    if (!detail) {
      removeLiveProduct(id)
      return null
    }

    const mapped = toMallProduct(detail)
    upsertLiveProduct(mapped)
    return mapped
  } catch {
    /* 서버에 닿지 못한 경우다. 목록에 남아 있으면 그걸로 보여 준다. */
    return getProductById(id) ?? null
  }
}

export async function fetchMallHomeSections() {
  await ensureLiveProducts()

  const deals = getDealProducts(8)
  const all = getAllProducts()
  const hero = deals[0] ?? all[0]
  const heroId = hero?.id

  return {
    /* 첫 상품은 히어로로 크게 보여주므로 특가 레일에서는 뺀다 */
    deals: deals.filter((item) => item.id !== heroId).slice(0, 8),
    best: getBestProducts(8),
    newest: getNewProducts(8),
    recommend: getRecommendProducts(8),
    hero,
  }
}
